import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  Divider,
  Paper
} from '@mui/material';
import BlogImage from './BlogImage';
import CustomEmoji from './CustomEmoji';
import EmojiPicker from './EmojiPicker';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Archive as ArchiveIcon,
  EmojiEmotions as EmojiIcon,
  ThumbUp as ThumbUpIcon,
  Favorite as FavoriteIcon,
  Celebration as CelebrationIcon,
  SentimentSatisfied as SmileIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from '../config/api';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  imageUrl: string | null;
  status: 'draft' | 'published' | 'archived';
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  author: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  reactions?: BlogReaction[];
  reactionStats?: ReactionStats;
}

interface BlogReaction {
  id: string;
  emoji: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface ReactionStats {
  [emoji: string]: {
    count: number;
    users: Array<{
      id: string;
      name: string;
    }>;
  };
}

const Blog: React.FC = () => {
  const { t } = useTranslation();
  const { user, token, hasPermission } = useAuth();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [blogForm, setBlogForm] = useState({
    title: '',
    content: '',
    status: 'draft' as 'draft' | 'published' | 'archived'
  });

  const emojiOptions = ['👍', '❤️', '🎉', '😊', '🔥', '👏', '💯', '🚀'];

  useEffect(() => {
    if (token) {
      fetchBlogPosts();
    }
  }, [token]);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(API_ENDPOINTS.BLOG_POSTS), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBlogPosts(data.blogPosts);
      } else {
        console.error('Failed to fetch blog posts');
        setError(t('blog.failedToFetchPosts'));
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setError(t('blog.errorFetchingPosts'));
    } finally {
      setLoading(false);
    }
  };

  const fetchReactions = async (postId: string) => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.BLOG_REACTIONS(postId)), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.reactionStats;
      }
    } catch (error) {
      console.error('Error fetching reactions:', error);
    }
    return {};
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreatePost = async () => {
    if (!blogForm.title.trim() || !blogForm.content.trim()) {
      setError(t('blog.titleRequired'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const formData = new FormData();
      formData.append('title', blogForm.title);
      formData.append('content', blogForm.content);
      formData.append('status', blogForm.status);
      
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      const response = await fetch(getApiUrl(API_ENDPOINTS.BLOG_POSTS), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error response:', errorData);
        
        // Display detailed error information from server
        if (errorData.details) {
          throw new Error(`${errorData.error}: ${errorData.details}`);
        } else if (errorData.error) {
          throw new Error(errorData.error);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }

      await fetchBlogPosts();
      setCreateDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error('Error creating blog post:', err);
      setError(err instanceof Error ? err.message : t('blog.failedToCreatePost'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPost = async () => {
    if (!selectedPost || !blogForm.title.trim() || !blogForm.content.trim()) {
      setError(t('blog.titleRequired'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const formData = new FormData();
      formData.append('title', blogForm.title);
      formData.append('content', blogForm.content);
      formData.append('status', blogForm.status);
      
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      const response = await fetch(getApiUrl(API_ENDPOINTS.BLOG_POST(selectedPost.id)), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('blog.failedToUpdatePost'));
      }

      await fetchBlogPosts();
      setEditDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error('Error updating blog post:', err);
      setError(err instanceof Error ? err.message : t('blog.failedToUpdatePost'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm(t('blog.deletePostConfirm'))) return;

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.BLOG_POST(postId)), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setBlogPosts(blogPosts.filter(post => post.id !== postId));
      } else {
        console.error('Failed to delete blog post');
      }
    } catch (error) {
      console.error('Error deleting blog post:', error);
    }
  };

  const handleStatusUpdate = async (postId: string, status: string) => {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.BLOG_STATUS(postId)), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        await fetchBlogPosts();
      } else {
        console.error('Failed to update blog post status');
      }
    } catch (error) {
      console.error('Error updating blog post status:', error);
    }
  };

  const handleReaction = async (postId: string, emoji: string) => {
    try {
      console.log('🔄 Adding reaction:', emoji, 'to post:', postId);
      
      const response = await fetch(getApiUrl(API_ENDPOINTS.BLOG_REACTIONS(postId)), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emoji })
      });

      if (response.ok) {
        console.log('✅ Reaction added successfully');
        // Refresh reactions for this post
        await fetchReactions(postId);
        // Refresh all blog posts to get updated reaction stats
        await fetchBlogPosts();
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to add reaction:', errorData);
        setError(errorData.error || t('blog.failedToAddReaction'));
      }
    } catch (error) {
      console.error('❌ Error adding reaction:', error);
      setError(t('blog.failedToAddReaction') + '. ' + t('blog.tryAgain'));
    }
  };

  const handleRemoveReaction = async (postId: string, emoji: string) => {
    try {
      console.log('🗑️ Removing reaction:', emoji, 'from post:', postId);
      
      const response = await fetch(getApiUrl(API_ENDPOINTS.BLOG_REACTION(postId, emoji)), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log('✅ Reaction removed successfully');
        // Refresh reactions for this post
        await fetchReactions(postId);
        // Refresh all blog posts to get updated reaction stats
        await fetchBlogPosts();
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to remove reaction:', errorData);
        setError(errorData.error || t('blog.failedToRemoveReaction'));
      }
    } catch (error) {
      console.error('❌ Error removing reaction:', error);
      setError(t('blog.failedToRemoveReaction') + '. ' + t('blog.tryAgain'));
    }
  };

  const hasUserReacted = (post: BlogPost, emoji: string) => {
    if (!user || !post.reactionStats?.[emoji]?.users) return false;
    return post.reactionStats[emoji].users.some(userInfo => userInfo.id === user.id);
  };

  const getUserReactions = (post: BlogPost) => {
    const userReactions: string[] = [];
    if (post.reactionStats && user) {
      Object.entries(post.reactionStats).forEach(([emoji, stats]) => {
        if (stats.users.some(u => u.id === user.id)) {
          userReactions.push(emoji);
        }
      });
    }
    return userReactions;
  };

  const resetForm = () => {
    setBlogForm({
      title: '',
      content: '',
      status: 'draft'
    });
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedPost(null);
  };

  const openEditDialog = (post: BlogPost) => {
    setSelectedPost(post);
    setBlogForm({
      title: post.title,
      content: post.content,
      status: post.status
    });
          setImagePreview(post.imageUrl ? `${API_BASE_URL}${post.imageUrl}` : null);
    setEditDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'published': return <CheckCircleIcon />;
      case 'draft': return <EditIcon />;
      case 'archived': return <ArchiveIcon />;
      default: return <EditIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('blog.title')}
        </Typography>
        {hasPermission('blog.publish') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            {t('blog.newPost')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: '1fr', 
          sm: 'repeat(2, 1fr)', 
          md: 'repeat(2, 1fr)', 
          lg: 'repeat(3, 1fr)' 
        }, 
        gap: { xs: 2, sm: 3 },
        alignItems: 'stretch'
      }}>
        {blogPosts.map((post) => {
          return (
            <Card key={post.id} sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 2,
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}>
              {post.imageUrl && (
                <BlogImage
                  src={post.imageUrl}
                  alt={post.title}
                  onLoad={() => {
                    console.log('✅ Image loaded successfully:', post.imageUrl);
                  }}
                  onError={() => {
                    console.error('❌ Image failed to load:', post.imageUrl);
                  }}
                />
              )}
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', flex: 1 }}>
                  {post.title}
                </Typography>
                <Chip
                  icon={getStatusIcon(post.status)}
                  label={post.status}
                  color={getStatusColor(post.status) as any}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                {post.content.length > 150 
                  ? `${post.content.substring(0, 150)}...` 
                  : post.content
                }
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
                  {post.author.firstName[0]}
                </Avatar>
                <Typography variant="caption" color="text.secondary">
                  {post.author.firstName} {post.author.lastName}
                </Typography>
              </Box>

              {/* User's Reactions Indicator */}
              {user && getUserReactions(post).length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    Your reactions:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {getUserReactions(post).map((emoji) => (
                      <Chip
                        key={emoji}
                        label={<CustomEmoji emoji={emoji} size={16} />}
                        size="small"
                        variant="outlined"
                        color="default"
                        onClick={() => handleRemoveReaction(post.id, emoji)}
                        sx={{ 
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          backgroundColor: 'transparent',
                          borderColor: 'divider',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            borderColor: 'error.main',
                            color: 'error.main'
                          }
                        }}
                        title={`Remove ${emoji} reaction`}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Reactions */}
              {post.reactionStats && Object.keys(post.reactionStats).length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {Object.entries(post.reactionStats).map(([emoji, stats]) => {
                    const userHasReacted = hasUserReacted(post, emoji);
                    return (
                      <Chip
                        key={emoji}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CustomEmoji emoji={emoji} size={16} />
                            <span>{stats.count}</span>
                          </Box>
                        }
                        size="small"
                        variant="outlined"
                        color="default"
                        onClick={() => userHasReacted ? handleRemoveReaction(post.id, emoji) : handleReaction(post.id, emoji)}
                        sx={{ 
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          borderColor: userHasReacted ? 'primary.main' : 'divider',
                          color: 'inherit',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            borderColor: userHasReacted ? 'error.main' : 'primary.main',
                            color: userHasReacted ? 'error.main' : 'primary.main'
                          }
                        }}
                        title={userHasReacted ? `Remove ${emoji} reaction` : `Add ${emoji} reaction`}
                      />
                    );
                  })}
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {new Date(post.createdAt).toLocaleDateString()}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* Emoji Reactions */}
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {/* Quick reaction buttons */}
                    {emojiOptions.slice(0, 3).map((emoji) => {
                      const userHasReacted = hasUserReacted(post, emoji);
                      return (
                        <Tooltip key={emoji} title={userHasReacted ? `Remove ${emoji} reaction` : `React with ${emoji}`}>
                          <IconButton
                            size="small"
                            onClick={() => userHasReacted ? handleRemoveReaction(post.id, emoji) : handleReaction(post.id, emoji)}
                            sx={{ 
                              fontSize: '1rem',
                              backgroundColor: 'transparent',
                              color: 'inherit',
                              border: '1px solid',
                              borderColor: userHasReacted ? 'primary.main' : 'divider',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                borderColor: userHasReacted ? 'error.main' : 'primary.main',
                                color: userHasReacted ? 'error.main' : 'primary.main'
                              }
                            }}
                          >
                            <CustomEmoji emoji={emoji} size={18} />
                          </IconButton>
                        </Tooltip>
                      );
                    })}
                    
                    {/* Emoji picker for more options */}
                    <EmojiPicker 
                      onEmojiSelect={(emoji) => handleReaction(post.id, emoji)}
                      disabled={false}
                    />
                  </Box>

                  {/* Admin Actions */}
                  {hasPermission('blog.publish') && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit Post">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(post)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Post">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeletePost(post.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}
      </Box>

      {/* Create Blog Post Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('blog.createNewBlogPost')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label={t('blog.postTitle')}
              value={blogForm.title}
              onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
              required
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  onChange={handleImageChange}
                />
                <label htmlFor="image-upload">
                  <Button variant="outlined" component="span" fullWidth>
                    Upload Image
                  </Button>
                </label>
                {imagePreview && (
                  <Box sx={{ mt: 1 }}>
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 4 }}
                    />
                  </Box>
                )}
              </Box>
              
              <Box sx={{ flex: 2 }}>
                <TextField
                  fullWidth
                  label="Content"
                  multiline
                  rows={8}
                  value={blogForm.content}
                  onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                  required
                />
              </Box>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={blogForm.status}
                onChange={(e) => setBlogForm({ ...blogForm, status: e.target.value as any })}
                label="Status"
              >
                <MenuItem value="draft">{t('blog.draft')}</MenuItem>
                <MenuItem value="published">{t('blog.published')}</MenuItem>
                <MenuItem value="archived">{t('blog.archived')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreatePost} 
            variant="contained" 
            disabled={submitting || !blogForm.title || !blogForm.content}
          >
            {submitting ? <CircularProgress size={20} /> : 'Create Post'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Blog Post Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Blog Post</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Title"
              value={blogForm.title}
              onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
              required
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="edit-image-upload"
                  type="file"
                  onChange={handleImageChange}
                />
                <label htmlFor="edit-image-upload">
                  <Button variant="outlined" component="span" fullWidth>
                    {imagePreview ? 'Change Image' : 'Upload Image'}
                  </Button>
                </label>
                {imagePreview && (
                  <Box sx={{ mt: 1 }}>
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 4 }}
                    />
                  </Box>
                )}
              </Box>
              
              <Box sx={{ flex: 2 }}>
                <TextField
                  fullWidth
                  label="Content"
                  multiline
                  rows={8}
                  value={blogForm.content}
                  onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                  required
                />
              </Box>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={blogForm.status}
                onChange={(e) => setBlogForm({ ...blogForm, status: e.target.value as any })}
                label="Status"
              >
                <MenuItem value="draft">{t('blog.draft')}</MenuItem>
                <MenuItem value="published">{t('blog.published')}</MenuItem>
                <MenuItem value="archived">{t('blog.archived')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleEditPost} 
            variant="contained" 
            disabled={submitting || !blogForm.title || !blogForm.content}
          >
            {submitting ? <CircularProgress size={20} /> : 'Update Post'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Blog; 